import { useCallback, useMemo } from "react";
import { RELEASE_NOTES } from "@/pages/ReleaseNotes/data";
import { filterReleaseNotesByFlags } from "@/pages/ReleaseNotes/releaseNoteFlags";
import type { ReleaseNote } from "@/pages/ReleaseNotes/types";
import { usePreference } from "@/contexts/PreferencesContext";
import { useRootLoader } from "@/hooks/useRootLoader";
import {
  resolveTenantFeatureFlag,
  type TenantFlagAuth,
} from "@/utils/tenantFeatureFlag";

/**
 * PRODUCT_DEV-1066: このテナントに見せるリリースノート（機能フラグで項目を絞り、空の版は落とす）。
 * ニュース画面の一覧と未読の判定は、必ずこれを通す（片方だけ絞ると「読む物が無いのに赤ポチ」になる）。
 */
export function useVisibleReleaseNotes(): ReleaseNote[] {
  const { auth } = useRootLoader();
  return useMemo(
    () =>
      filterReleaseNotesByFlags(RELEASE_NOTES, (key) =>
        resolveTenantFeatureFlag(auth as TenantFlagAuth | null, key),
      ),
    [auth],
  );
}

/**
 * お知らせの既読管理。
 *
 * PRODUCT_DEV-762 では既読を localStorage にしか持っていなかったため、書き込んでも
 * 他のコンポーネント（メニュー行・タブバー・ニュース画面）が再レンダーされず、
 * モジュールレベルの購読を自前で実装していた。
 *
 * PRODUCT_DEV-699 で既読を個人設定（サーバ）へ移したことで、値は Context が持つ状態に
 * なった。**購読は Context の再レンダーがそのまま担うので自前の仕組みは要らない。**
 * 端末をまたいで既読が引き継がれる副次効果もある（PC で読んだお知らせがスマホでも既読）。
 */
function getLatestVersion(notes: readonly ReleaseNote[]): string {
  // Minizo ビルドは vite.config.ts の alias で RELEASE_NOTES が空配列に差し替わる
  // （PRODUCT_DEV-748）。したがって Minizo では常に "" ＝未読なしになる。
  // PRODUCT_DEV-1066: 最新は「このテナントに見える版」の最新。項目が全部フラグの裏の版は数えない。
  return notes.length > 0 ? notes[0].version : "";
}

export function useReleaseNotes() {
  const [lastSeenVersion, setLastSeenVersion] = usePreference(
    "lastSeenReleaseVersion",
    "",
  );

  const latest = getLatestVersion(useVisibleReleaseNotes());

  const markAsRead = useCallback(() => {
    if (!latest) return;
    if (lastSeenVersion === latest) return;
    setLastSeenVersion(latest);
  }, [latest, lastSeenVersion, setLastSeenVersion]);

  return {
    hasUnread: latest !== "" && lastSeenVersion !== latest,
    markAsRead,
    lastSeenVersion: lastSeenVersion || null,
  };
}
